/**
 * Queue calling operations (Redmine #18575, design §4–§5). Doctor/nurse/reception quick actions
 * that drive the one authoritative queue state — real backend `/api/v1/queue/*`.
 */
import { apiClient } from "./client";

/** Result of "gọi tiếp"; `found=false` when no eligible patient was waiting (do NOT +1). */
export interface CalledPatient {
  appointmentId: string | null;
  registrationNo: number | null;
  roomNo: number | null;
  found: boolean;
}

export const queueOpsApi = {
  /** Gọi tiếp — atomically call the next eligible WAITING patient (branch, optional room). */
  callNext(branchId: string, roomId?: string | null): Promise<CalledPatient> {
    return apiClient
      .post<CalledPatient>("/v1/queue/call-next", {
        branchId,
        roomId: roomId ?? null,
      })
      .then((r) => r.data);
  },
  /** Hoàn tất & gọi tiếp — call the next patient into the room of this appointment (server resolves the room). */
  callNextForRoom(appointmentId: string): Promise<CalledPatient> {
    return apiClient
      .post<CalledPatient>(`/v1/queue/${appointmentId}/next`)
      .then((r) => r.data);
  },
  /** Gọi lại — re-announce the current patient (no state change). */
  recall(appointmentId: string): Promise<void> {
    return apiClient
      .post(`/v1/queue/${appointmentId}/recall`)
      .then(() => undefined);
  },
  /** Bỏ qua — mark SKIPPED (recallable via resume). */
  skip(appointmentId: string): Promise<void> {
    return apiClient.post(`/v1/queue/${appointmentId}/skip`).then(() => undefined);
  },
  /** Tạm giữ — pull out of the call order without completing/no-showing (e.g. sent for labs). */
  hold(appointmentId: string): Promise<void> {
    return apiClient.post(`/v1/queue/${appointmentId}/hold`).then(() => undefined);
  },
  /** Resume-Hold — return a parked (HELD/SKIPPED) patient to WAITING. */
  resume(appointmentId: string): Promise<void> {
    return apiClient
      .post(`/v1/queue/${appointmentId}/resume`)
      .then(() => undefined);
  },
  /** Ưu tiên — set/clear priority (gọi trước; STT gốc giữ nguyên). */
  setPriority(appointmentId: string, priority: boolean): Promise<void> {
    return apiClient
      .post(`/v1/queue/${appointmentId}/priority`, { priority })
      .then(() => undefined);
  },
};

/**
 * Which rooms the signed-in user works in (#18882).
 *
 * @property singleRoomBranch the branch has one clinical room — show no room UI at all.
 * @property canViewAll holder of `queue.coordinate`; without it the "all rooms" option is not
 *   rendered, rather than rendered and then refused.
 */
export interface RoomScope {
  rooms: { id: string; name: string }[];
  singleRoomBranch: boolean;
  canViewAll: boolean;
  unassignedVisible: boolean;
}

export const roomScopeApi = {
  get(branchId?: string | null): Promise<RoomScope> {
    return apiClient
      .get<RoomScope>("/v1/queue/room-scope", {
        params: branchId ? { branchId } : {},
      })
      .then((r) => r.data);
  },
};

export const roomScopeQueryKeys = {
  scope: (branchId?: string | null) => ["queue", "room-scope", branchId ?? ""] as const,
};
